import { ChangeEvent, Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SearchInputBar } from '@/lib/components/SearchInputBar';
import { AddCirclePlusIcon } from '@/lib/icon';
import { IconSvg, Text } from '@/lib/v2/components';
import { Divide, Flex } from '@/lib/v2/components/Layout/Stack';
import { LoadingIcon } from '@/lib/v2/icons/animated';

import { useSortedItems } from '@/src/application/hooks/useSortedItems';
import { useResetSearch } from '@/src/infrastructure/services/useResetSearch';

interface ItemSearchProps {
  SearchPlaceHolder?: string;
  itemList: Array<string>;
  title?: string;
  subTitle?: string;
  selectedItems: string[];
  setSelectedItems?: Dispatch<SetStateAction<string[]>>;
  setUnselectedItems?: Dispatch<SetStateAction<string[]>>;
  maxItems?: number;
  onCreate?: (value: string) => void;
  isLoading?: boolean;
}

export const ItemSearch = ({
  SearchPlaceHolder = '',
  itemList,
  title = '',
  subTitle = '',
  selectedItems,
  setSelectedItems,
  setUnselectedItems,
  maxItems,
  onCreate,
  isLoading,
}: ItemSearchProps) => {
  const { t } = useTranslation();
  const [searchItem, setSearchItem] = useState('');
  const [showMaxItemsMessage, setShowMaxItemsMessage] = useState<boolean>(false);
  const { resetFilter } = useResetSearch({ setSearchValue: setSearchItem as () => void });

  const createNewItem = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSearchItem(value);
  };

  const foundItemCoincidences = (item: string) => {
    if (searchItem === '') {
      return true;
    } else {
      const regex = new RegExp(searchItem, 'i');
      return regex.test(item);
    }
  };

  const foundItemInVector = (items: Array<string>, searchItemVal: string) => {
    const finder = items.find((item) => {
      return item.toLowerCase() === searchItemVal.toLowerCase();
    });
    return finder !== undefined ? true : false;
  };

  useEffect(() => {
    if (maxItems) {
      disableInputs(selectedItems.length >= maxItems);
      setShowMaxItemsMessage(selectedItems.length >= maxItems);
    }
  }, [selectedItems]);

  const checkboxHandle = (e: ChangeEvent<HTMLInputElement>, items: string[]): void => {
    const checkedValue = e.target.value;
    let itemsAux = [...selectedItems];

    if (e.target.checked) {
      if (!itemsAux.includes(checkedValue)) {
        itemsAux.push(checkedValue);
      }
    } else {
      itemsAux = itemsAux.filter((item) => item !== checkedValue);
    }

    setSelectedItems && setSelectedItems(itemsAux);
    const unselectedItemsAux = items.filter((item) => !itemsAux.includes(item));
    setUnselectedItems && setUnselectedItems(unselectedItemsAux);
  };

  const disableInputs = (value: boolean) => {
    const checkbox: any = document.getElementsByClassName('_modal_checkbox_');
    if (checkbox) {
      for (let i = 0; i < checkbox.length; i++) {
        if (!checkbox[i].checked) {
          checkbox[i].disabled = value;
          checkbox[i].parentElement.style.opacity = value ? 0.4 : 1;
        }
      }
    }
  };

  const sortedItems: string[] = useSortedItems(selectedItems, itemList);

  const handleCreate = useCallback(() => {
    onCreate?.(searchItem);
  }, [onCreate, searchItem]);

  return (
    <div className="w-full">
      <section className="mb-5 max-w-lg">
        <SearchInputBar
          cleanBox={resetFilter}
          placeHolder={SearchPlaceHolder}
          onChange={createNewItem}
        />
      </section>
      <section>
        {showMaxItemsMessage && (
          <span className="text-14">
            {t('ITEM_SEARCH_MODAL.MaxSelection')} {maxItems},{' '}
            {t('ITEM_SEARCH_MODAL.WorkingOnIncreasingNumber')}{' '}
          </span>
        )}
      </section>
      <section>
        {searchItem?.length > 0 && !foundItemInVector(itemList, searchItem) && (
          <section>
            {subTitle !== '' && (
              <Text
                as="h3"
                className="uppercase"
                color="primary"
                fontWeight="normal"
                variant="text"
              >
                {subTitle}
              </Text>
            )}
            <div className="mb-4 flex flex-row items-center justify-between">
              <Text className="!text-12 xl:!text-14 2xl:!text-16">{searchItem}</Text>
              {isLoading ? (
                <LoadingIcon />
              ) : (
                <IconSvg
                  className="!h-6 !w-6"
                  id="create-item-button"
                  svgComponent={<AddCirclePlusIcon />}
                  onClick={handleCreate}
                />
              )}
            </div>
          </section>
        )}
        <section className={'visible-scrollbar max-h-60 min-h-min min-w-200 overflow-y-auto pb-1'}>
          {title !== '' && (
            <Text as="h3" className="uppercase" color="primary" fontWeight="normal" variant="text">
              {title}
            </Text>
          )}
          <div className="mt-2 divide-y ">
            <Divide>
              <Flex column itemAlignment="stretch">
                <>
                  {sortedItems
                    .filter((element: string) => foundItemCoincidences(element))
                    .map((item) => {
                      return (
                        <div key={item} className="h-[26px] lg:h-[30px] xl:h-[38px] 2xl:h-[48px]">
                          <Flex alignment="start" itemAlignment="center">
                            <input
                              checked={selectedItems.includes(item)}
                              className="_modal_checkbox_ mr-3 cursor-pointer rounded border-gray-300 bg-gray-100 text-blue-600 lg:h-3 lg:w-3 xl:h-3 xl:w-3 2xl:h-4 2xl:w-4"
                              id="checked-checkbox"
                              type="checkbox"
                              value={item}
                              onChange={(e) => checkboxHandle(e, [item])}
                            />
                            <span className="text-[10px] lg:text-12 xl:text-14 2xl:text-16 ">
                              {item}
                            </span>
                          </Flex>
                        </div>
                      );
                    })}
                </>
              </Flex>
            </Divide>
          </div>
        </section>
      </section>
    </div>
  );
};
